import { trackIt } from '@vfde-sails/tracking';
import { startAppListening } from '../../app/listener';
import { getTradeInTrackingPayload } from '../../helpers/tracking';
import {
    selectIsTradeIn,
    selectSelectedTradeInDevice,
} from './selectors';
import {
    deleteSelectedTradeInDevice,
    selectIsTradeInSelected,
    setIsTradeInSelected,
    setSelectedTradeInDeviceId,
} from './slice';
import { updateStorage } from '@vfde-sails/storage';
import { SailsDeviceDetailsStorage } from '../../interfaces/storage';
import { SAILS_BNT_STORAGE } from '@vfde-sails/constants';
import {
    APP,
    CONTAINER_TRADEIN,
} from '../../app/constants';
import { getTradeInDeviceFromStorage } from './helpers/getTradeIn';
import { setTradeIn } from '../app/slice';

export const listeners = {
    /**
     * Adds the listener for the `setIsTradeInSelected` action.
     * It then tracks and dispatches the `setIsTradeIn` action.
     */
    setIsTradeInSelected: () => startAppListening({
        actionCreator: setIsTradeInSelected,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const isTradeInSelected = selectIsTradeInSelected(state);
            const selectedTradeInDevice = selectSelectedTradeInDevice(state);

            const {
                trackingType,
                trackingPayload,
            } = getTradeInTrackingPayload(isTradeInSelected, selectedTradeInDevice);

            trackIt(trackingType, trackingPayload);

            const isTradeIn = selectIsTradeIn(state);
            listenerApi.dispatch(setTradeIn(isTradeIn));
        },
    }),
    /**
     * Adds the listener for the `setSelectedTradeInDeviceId` action.
     * It then updates the storage, tracks the device selection
     * and dispatches the `setIsTradeIn` action.
     */
    setSelectedTradeInDeviceId: () => startAppListening({
        actionCreator: setSelectedTradeInDeviceId,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const selectedTradeInDevice = selectSelectedTradeInDevice(state);
            const hasTradeInDevice = !!selectedTradeInDevice;

            updateStorage<SailsDeviceDetailsStorage>(
                SAILS_BNT_STORAGE,
                {
                    [APP]: {
                        [CONTAINER_TRADEIN]: {
                            tradeInDevice: selectedTradeInDevice,
                        },
                    },
                },
                {
                    shouldDeepMerge: true,
                },
            );

            if (hasTradeInDevice) {
                // track a tradeIn-reveal event on device selection (not on deselection)

                const {
                    trackingType,
                    trackingPayload,
                } = getTradeInTrackingPayload(true, selectedTradeInDevice);

                trackIt(trackingType, trackingPayload);
            }

            listenerApi.dispatch(setTradeIn(hasTradeInDevice));
        },
    }),
    /**
     * Adds the listener for the `deleteSelectedTradeInDevice` action.
     * It then updates the storage, tracks the device deletion
     * and dispatches the `setIsTradeIn` action.
     */
    deleteSelectedTradeInDevice: () => startAppListening({
        actionCreator: deleteSelectedTradeInDevice,
        effect: (_action, listenerApi) => {
            // first get old device from storage
            const deletedTradeInDevice = getTradeInDeviceFromStorage()!;

            // then update the storage with null value
            updateStorage<SailsDeviceDetailsStorage>(
                SAILS_BNT_STORAGE,
                {
                    [APP]: {
                        [CONTAINER_TRADEIN]: null,
                    },
                },
                {
                    shouldDeepMerge: true,
                },
            );

            const {
                trackingType,
                trackingPayload,
            } = getTradeInTrackingPayload(false, deletedTradeInDevice);

            trackIt(trackingType, trackingPayload);

            listenerApi.dispatch(setTradeIn(false));
        },
    }),
};

/**
 * Starts all listeners
 */
export const startListeners = () => {
    const unsubscribeListeners = Object.values(listeners).map(listener => listener());

    return () => {
        unsubscribeListeners.forEach(unsubscribeListener => unsubscribeListener());
    };
};
